import React, { useEffect, useState, useRef } from 'react';

/**
 * Lightweight replacement for `mui-image` (which was abandoned and depended on
 * `@mui/styles`). Renders an <img> with fade-in on load, an optional loading
 * overlay, and onLoad/onError forwarding. Used by the EImage wrapper and
 * directly by Media.
 *
 * Supported props (matching the subset of mui-image we actually used):
 *   - src, alt
 *   - width, height
 *   - onLoad, onError
 *   - loading: ReactNode rendered (centered) while the image is loading
 *   - imageStyle: styles merged into the inner <img>
 *   - style, className, ...rest: applied to the wrapper / img passthrough
 */
// Props consumed by the index.js Image wrapper, never valid on <img>.
const WRAPPER_ONLY_PROPS = new Set([
  'useNaturalDims',
  'autoReload',
  'autosize',
  'unsetHeight',
  'unsetWidth',
  'canSelect',
  'imageSel',
  'autoLoad',
  'autoSize',
]);

const Image = ({
  src,
  alt,
  width,
  height,
  onLoad,
  onError,
  loading,
  imageStyle,
  style,
  className,
  ...rest
}) => {
  // Drop wrapper-only props that would otherwise leak to the DOM via {...rest}.
  const imgRest = Object.fromEntries(
    Object.entries(rest).filter(([k]) => !WRAPPER_ONLY_PROPS.has(k))
  );
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // PFM-2600 follow-up: Reset fade-in when the source changes, but immediately
  // mark as loaded if the browser already has the image cached (img.complete +
  // naturalWidth > 0). Without this check, browser-cached images that fire
  // their `load` event synchronously when src is set (before React attaches
  // the onLoad listener) get stuck with opacity:0 — producing the "after
  // editing one photo, all other thumbnails disappear" bug Sam reported,
  // since the cached thumbs of the unedited photos race the load event.
  useEffect(() => {
    const node = imgRef.current;
    if (node && node.complete && node.naturalWidth > 0) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [src]);

  const handleLoad = (e) => {
    setLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    onError?.(e);
  };

  const showLoading = loading != null && loading !== false && !loaded;

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: width ?? '100%',
        height: height ?? '100%',
        ...style,
      }}
    >
      {showLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          {loading}
        </div>
      )}
      {src && (
        <img
          ref={imgRef}
          src={src}
          alt={alt ?? ''}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 300ms ease-in',
            ...imageStyle,
          }}
          {...imgRest}
        />
      )}
    </div>
  );
};

export default Image;
